import {
	USER_TYPE_CUSTOMER,
	USER_TYPE_THIRDPARTY,
	ROLE_CLIENT,
	ROLE_GUEST_NOTARY,
	USER_CONTEXT_CUSTOMER,
	USER_CONTEXT_NOTARY,
	USER_CONTEXT_INVITED_NOTARY,
	USER_CONTEXT_THIRD_PARTY,
	isCustomer,
	isGuestNotary,
} from "le-coffre-resources/dist/MemberTypes";
import type { UserContext } from "@Front/Utils/zipDownloadHelpers";
import { getActiveSessionFamily } from "@Front/Utils/lecoffreActiveSessionFamily";
import {
	getLeCoffreCustomerAccessTokenCookie,
	getLeCoffreThirdPartyAccessTokenCookie,
} from "@Front/Utils/lecoffreSessionCookieHelpers";

export type PermissionContextLike = {
	userType?: string | null;
	globalRole?: string | null;
	activeOfficeUid?: string | null;
	isGuest?: boolean | null;
};

export type ResolvedUserContext = {
	userContext: UserContext;
	isThirdParty: boolean;
	isCustomer: boolean;
	isInvitedNotary: boolean;
};

function normalizeRoleValue(value: string | null | undefined): string {
	return value?.toLowerCase().replace(/[_-]/g, "") ?? "";
}

/** Check if value is thirdparty (accepts thirdparty, third_party, third-party) */
function isThirdPartyValue(value: string | null | undefined): boolean {
	return value === USER_TYPE_THIRDPARTY || normalizeRoleValue(value) === "thirdparty";
}

export function isThirdPartyContext(permissionContext: PermissionContextLike | null | undefined): boolean {
	const userType = permissionContext?.userType;
	const globalRole = permissionContext?.globalRole;
	return isThirdPartyValue(userType) || isThirdPartyValue(globalRole);
}

export function isCustomerContext(permissionContext: PermissionContextLike | null | undefined): boolean {
	if (isThirdPartyContext(permissionContext)) {
		return false;
	}
	const userType = permissionContext?.userType;
	const globalRole = permissionContext?.globalRole;
	return (
		userType === USER_TYPE_CUSTOMER ||
		globalRole === USER_TYPE_CUSTOMER ||
		globalRole === ROLE_CLIENT ||
		isCustomer(userType) ||
		isCustomer(globalRole)
	);
}

export function isInvitedNotaryContext(permissionContext: PermissionContextLike | null | undefined): boolean {
	const userType = permissionContext?.userType;
	const globalRole = permissionContext?.globalRole;
	return (
		permissionContext?.isGuest === true ||
		permissionContext?.activeOfficeUid === "GUEST_FOLDERS" ||
		userType === ROLE_GUEST_NOTARY ||
		globalRole === ROLE_GUEST_NOTARY ||
		isGuestNotary(userType) ||
		isGuestNotary(globalRole)
	);
}

/**
 * Session-family fallback when permissionContext is empty.
 * A notary (including invited / GUEST_FOLDERS) family must not be treated as client space
 * even if leftover customer cookies exist in the same browser.
 */
export function isClientSpaceFromSessionFamily(
	family: string | null,
	hasCustomerOrThirdPartyToken: boolean,
): boolean {
	if (family === "notary") {
		return false;
	}
	if (family === "customer" || family === "thirdparty") {
		return true;
	}
	return hasCustomerOrThirdPartyToken;
}

function hasClientSpaceSessionToken(): boolean {
	return isClientSpaceFromSessionFamily(
		getActiveSessionFamily(),
		Boolean(getLeCoffreCustomerAccessTokenCookie() || getLeCoffreThirdPartyAccessTokenCookie()),
	);
}

/**
 * Viewer uses customer Files APIs: customer, third party, or invited notary (guest JWT on customer FilesNotary).
 * Falls back to the live client session when permissionContext is still empty
 * (e.g. customer landed on a `/folders/...` page after a wrong view link).
 * Invited notaries with a populated guest permissionContext stay on this path (pre-existing guest FilesNotary).
 */
export function isClientSpaceViewer(permissionContext: PermissionContextLike | null | undefined): boolean {
	if (
		isThirdPartyContext(permissionContext) ||
		isCustomerContext(permissionContext) ||
		isInvitedNotaryContext(permissionContext)
	) {
		return true;
	}
	return hasClientSpaceSessionToken();
}

function resolveUserContextType(flags: {
	isThirdParty: boolean;
	isCustomerResolved: boolean;
	isInvitedNotary: boolean;
}): UserContext {
	if (flags.isThirdParty) {
		return USER_CONTEXT_THIRD_PARTY;
	}
	if (flags.isCustomerResolved) {
		return USER_CONTEXT_CUSTOMER;
	}
	if (flags.isInvitedNotary) {
		return USER_CONTEXT_INVITED_NOTARY;
	}
	return USER_CONTEXT_NOTARY;
}

export function resolveUserContext(permissionContext: PermissionContextLike | null | undefined): ResolvedUserContext {
	const isThirdParty = isThirdPartyContext(permissionContext);
	const isCustomerResolved = isCustomerContext(permissionContext);
	const isInvitedNotary = isInvitedNotaryContext(permissionContext);
	return {
		userContext: resolveUserContextType({ isThirdParty, isCustomerResolved, isInvitedNotary }),
		isThirdParty,
		isCustomer: isCustomerResolved,
		isInvitedNotary,
	};
}
